import { LogOut, RefreshCw, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import logoImage from '@/assets/zen-logo2.png';

export function WebHeader() {
  const { user, properties, activePropertyId, setActiveProperty, logout, refreshProperties } = useAuthStore();
  const [isPropertyMenuOpen, setIsPropertyMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const activeProperty = properties.find((p) => p.id === activePropertyId);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshProperties();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <header className="h-14 flex items-center justify-between px-4 border-b border-border bg-surface/50 backdrop-blur-sm relative z-50">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#1E293B] to-[#0F1623] border border-white/10 flex items-center justify-center overflow-hidden shadow-[0_0_15px_-5px_rgba(59,130,246,0.3)]">
            <img
              src={logoImage}
              alt="ChartZen"
              className="w-6 h-6 object-contain drop-shadow-[0_0_4px_rgba(59,130,246,0.4)]"
            />
          </div>
          <h1 className="text-lg tracking-tight">
            <span className="font-light">CHART</span>
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-slate-400">ZEN</span>
          </h1>
        </div>
      </div>

      {/* Property Selector */}
      <div className="flex items-center gap-2">
        <div className="relative">
          <button
            onClick={() => setIsPropertyMenuOpen(!isPropertyMenuOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-surface hover:bg-surface-hover border border-border text-sm"
          >
            <span className="max-w-[200px] truncate">
              {activeProperty?.name || 'Select property'}
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>

          {isPropertyMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsPropertyMenuOpen(false)}
              />
              <div className="absolute top-full right-0 mt-1 w-64 rounded-lg border border-border bg-surface shadow-lg z-50">
                <div className="p-2 max-h-80 overflow-y-auto">
                  {properties.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No properties found
                    </div>
                  ) : (
                    properties.map((property) => (
                      <button
                        key={property.id}
                        onClick={() => {
                          setActiveProperty(property.id);
                          setIsPropertyMenuOpen(false);
                        }}
                        className={cn(
                          'w-full text-left px-3 py-2 rounded-md text-sm',
                          property.id === activePropertyId
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-surface-hover'
                        )}
                      >
                        <div className="font-medium truncate">{property.name}</div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {property.ga4Property && <span>GA4</span>}
                          {property.gscSite && <span>GSC</span>}
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <div className="border-t border-border p-2">
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm hover:bg-surface-hover disabled:opacity-50"
                  >
                    <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
                    <span>Refresh properties</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User menu */}
        <div className="flex items-center gap-2 pl-2 border-l border-border">
          {user?.picture && (
            <img
              src={user.picture}
              alt={user.name || ''}
              className="w-8 h-8 rounded-full"
            />
          )}
          <div className="hidden sm:block">
            <div className="text-sm font-medium">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.email}</div>
          </div>
          <button
            onClick={() => logout()}
            className="p-2 rounded-md hover:bg-surface-hover text-muted-foreground hover:text-foreground"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
